import axios from "axios";
import { useEffect, useState } from "react";
import {
  Spinner,
  Table,
  Form,
  Pagination,
  Badge,
  Button,
  Modal,
  Row,
  Col,
} from "react-bootstrap";
import { getURLbyEndPoint } from "../../../store/api";
import { formatDateTimeStandard } from "../../../components/basic/simpleFunctions";
import Layout from "../../../components/Layouts/Layouts/Layout";
import MasterNavigationBar from "../../../components/Layouts/Layouts/MasterServicesLayout";
import DashboardHeader from "../../../components/basic/DashboardHeader";
import {
  ConfirmModal,
  PopUpModalButton,
  AdvancedPopUpModalButton,
} from "../../../components/basic/fileViewModal";
import { jsPDF } from "jspdf";
import CERTIFICATE_OF_PARTICIPATION from "../../../assets/certificate/CERTIFICATE_OF_PARTICIPATION.png";

const ViewEventBooking = () => {
  const [bookings, setBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selDay, setSelDay] = useState("");
  const [selEventType, setSelEventType] = useState("");
  const [selEventTitle, setSelEventTitle] = useState("");
  const [reqStatus, setReqStatus] = useState("Processing");
  const [appDisApi, setAppDisApi] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [showCertModal, setShowCertModal] = useState(false);
  const [certModalItem, setCertModalItem] = useState(null);

  const [customIssueDate, setCustomIssueDate] = useState("");
  const [signatureImage, setSignatureImage] = useState(null);

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSignatureImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Simple functions
  const handleInputChange = (itemId, name, value) => {
    setBookings((prevData) => {
      const updatedData = prevData.map((item) => {
        if (item._id === itemId) {
          return {
            ...item,
            [name]: value,
          };
        }
        return item;
      });
      return updatedData;
    });
  };

  const handleApproveRejectBooking = async (bookingId, isApproved) => {
    setAppDisApi(true);
    const bookingData = bookings.find((b) => b._id === bookingId);

    if (!bookingData.remark || bookingData.remark.trim() === "") {
      alert("Please enter a remark before proceeding.");
      setAppDisApi(false);
      return;
    }

    const endpoint = isApproved ? "approveEventBooking" : "rejectEventBooking";

    try {
      const payload = { bookingId, remark: bookingData.remark };
      const headers = { "Content-Type": "application/json" };

      const res = await axios.post(getURLbyEndPoint(endpoint), payload, {
        headers,
      });

      if (res.data.status) {
        alert(res.data.message);
        fetchBookings();
      } else {
        alert(res.data.message);
      }
    } catch (error) {
      console.error("Error updating booking status:", error);
      alert("An error occurred while handling booking status.");
    } finally {
      setAppDisApi(false);
    }
  };

  const handleApproveCertificate = async (bookingId) => {
    setAppDisApi(true);
    const bookingData = bookings.find((b) => b._id === bookingId);

    if (!bookingData.certificateBlob) {
      alert("Certificate has not been generated yet.");
      setAppDisApi(false);
      return;
    }

    try {
      const payload = new FormData();
      payload.append("bookingId", bookingId);
      payload.append(
        "certificate",
        bookingData.certificateBlob,
        `certificate_${bookingId}.pdf`,
      );

      const res = await axios.post(
        getURLbyEndPoint("approveEventCertificate"),
        payload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res.data.status) {
        alert(res.data.message);
        setShowCertModal(false);
        setCertModalItem(null);
        fetchBookings();
      } else {
        alert(res.data.message);
      }
    } catch (error) {
      console.error("Error approving certificate:", error);
      alert("An error occurred while approving the certificate.");
    } finally {
      setAppDisApi(false);
    }
  };

  const handleGenerateEventCert = (item) => {
    const pdf = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "landscape",
    });

    const imgWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = pdf.internal.pageSize.getHeight();
    pdf.addImage(
      CERTIFICATE_OF_PARTICIPATION,
      "PNG",
      0,
      0,
      imgWidth,
      imgHeight,
    );

    pdf.setTextColor("#545454");
    pdf.setFontSize(36.7);
    pdf.setFont("Zing Rust Base", "bold");
    const name = item.fullName.toUpperCase();
    const nameWidth = pdf.getTextWidth(name);
    pdf.text(name, (imgWidth - nameWidth) / 2, 83);

    const speaker = item.eventId?.speaker || "Speaker";
    const d = new Date(item.eventId?.eventDate);
    const day = d.toLocaleDateString("en-GB", { day: "numeric" });
    const month = d.toLocaleDateString("en-GB", { month: "long" });
    const year = d.toLocaleDateString("en-GB", { year: "numeric" });
    const weekday = d.toLocaleDateString("en-GB", { weekday: "long" });
    const dateFormatted = `${day} ${month} ${year} (${weekday})`;
    const mode = item.eventId?.location || "Online";

    pdf.setTextColor("#313131");
    pdf.setFontSize(15);
    pdf.setFont("Codec Pro", "bold");
    const subText = `${speaker}, on ${dateFormatted} | ${mode} Mode`;
    const subTextWidth = pdf.getTextWidth(subText);
    pdf.text(subText, (imgWidth - subTextWidth) / 2, 128);

    let finalIssueDate = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    if (customIssueDate) {
      const d = new Date(customIssueDate);
      if (!isNaN(d.getTime())) {
        finalIssueDate = d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }
    }

    pdf.setFontSize(14);
    pdf.text(finalIssueDate, 66, 155);

    if (signatureImage) {
      const format = signatureImage
        .substring("data:image/".length, signatureImage.indexOf(";base64"))
        .toUpperCase();
      const validFormat =
        format === "JPEG" || format === "JPG" ? "JPEG" : "PNG";
      pdf.addImage(signatureImage, validFormat, 199, 166, 25, 15);
    }

    const blobPdf = pdf.output("blob", { encoding: "raw" });
    const realBlob = new Blob([blobPdf], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(realBlob);

    // We update the local item object temporarily if needed, but immediately pop it out into Modal payload
    const updatedItem = {
      ...item,
      certificateBlob: realBlob,
      certificateUrl: blobUrl,
      remark: "Automatically Generated Certificate",
    };

    setBookings((prevData) =>
      prevData.map((b) => (b._id === item._id ? updatedItem : b)),
    );

    // Trigger the custom preview modal directly after regeneration
    setCertModalItem(updatedItem);
    setShowCertModal(true);
  };

  // Initial Fetch
  const fetchBookings = async () => {
    try {
      const response = await axios.get(getURLbyEndPoint("getAllEventBookings"));
      if (response.data.status) {
        const data = response.data.data;
        // Map status for UI filtering (Pending -> Processing)
        const modifiedData = data.map((item) => ({
          ...item,
          uiStatus: item.status === "Pending" ? "Processing" : item.status,
          remark: item.remark || "",
        }));
        setBookings(modifiedData.reverse());
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const calculateStartDate = (days) => {
    const today = new Date();
    const startDate = new Date(today);

    if (days === "yesterday") {
      startDate.setDate(today.getDate() - 1);
    } else if (days === "7days") {
      startDate.setDate(today.getDate() - 7);
    } else if (days === "14days") {
      startDate.setDate(today.getDate() - 14);
    }

    startDate.setHours(0, 0, 0, 0);
    return startDate;
  };

  const uniqueEventTypes = [
    ...new Set(bookings.map((b) => b.eventId?.eventType).filter(Boolean)),
  ];

  const uniqueEventTitles = [
    ...new Set(
      bookings
        .filter((b) =>
          selEventType ? b.eventId?.eventType === selEventType : true,
        )
        .map((b) => b.eventId?.title)
        .filter(Boolean),
    ),
  ];

  // Filtering
  const filteredData = bookings.filter((data) => {
    // Status Filter
    if (reqStatus !== "" && data.uiStatus !== reqStatus) {
      return false;
    }

    // Event Type Filter
    if (selEventType !== "" && data.eventId?.eventType !== selEventType) {
      return false;
    }

    // Event Title Filter
    if (selEventTitle !== "" && data.eventId?.title !== selEventTitle) {
      return false;
    }

    // Search Filter
    if (searchTerm !== "") {
      const lowerSearch = searchTerm.toLowerCase();
      const matchName = data.fullName?.toLowerCase().includes(lowerSearch);
      const matchEmail = data.email?.toLowerCase().includes(lowerSearch);
      const matchOrg = data.organizationName
        ?.toLowerCase()
        .includes(lowerSearch);
      const matchEvent = data.eventId?.title
        ?.toLowerCase()
        .includes(lowerSearch);
      if (!matchName && !matchEmail && !matchOrg && !matchEvent) return false;
    }

    // Date Filter
    if (selDay !== "") {
      const startDate = calculateStartDate(selDay);
      const createdAt = new Date(data.createdAt);
      if (createdAt < startDate) return false;
    }

    return true;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentFiltered = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(p + 1, totalPages));

  // Calculate the range of pages to display
  const calculateRange = (currentPage, totalPages) => {
    let startPage = Math.max(1, currentPage - 5);
    let endPage = Math.min(totalPages, currentPage + 4);

    if (endPage - startPage < 9) {
      if (startPage === 1) {
        endPage = Math.min(10, totalPages);
      } else if (endPage === totalPages) {
        startPage = Math.max(totalPages - 9, 1);
      }
    }
    return { startPage, endPage };
  };
  const { startPage, endPage } = calculateRange(currentPage, totalPages);

  useEffect(() => {
    fetchBookings();
  }, []);

  return (
    <Layout>
      <MasterNavigationBar>
        <div className="d-flex flex-column px-3">
          <DashboardHeader
            heading="EVENTS / CHAMBER EVENT BOOKING / VIEW BOOKING"
            backUrl="/chamber-event"
          />
          <hr />

          <div className="bg-light p-3 rounded mb-4 mt-4 shadow-sm border">
            <Row className="align-items-center g-3">
              <Col xs={12} xl={4}>
                <div className="fw-bold mb-2 small text-muted">
                  Filter by Status:
                </div>
                <div className="d-flex flex-wrap gap-3">
                  <Form.Check
                    inline
                    label="All"
                    type="radio"
                    checked={reqStatus === ""}
                    onChange={() => setReqStatus("")}
                    className="m-0"
                  />
                  <Form.Check
                    inline
                    label="Processing"
                    type="radio"
                    checked={reqStatus === "Processing"}
                    onChange={() => setReqStatus("Processing")}
                    className="m-0 text-warning"
                  />
                  <Form.Check
                    inline
                    label="Approved"
                    type="radio"
                    checked={reqStatus === "Approved"}
                    onChange={() => setReqStatus("Approved")}
                    className="m-0 text-success"
                  />
                  <Form.Check
                    inline
                    label="Rejected"
                    type="radio"
                    checked={reqStatus === "Rejected"}
                    onChange={() => setReqStatus("Rejected")}
                    className="m-0 text-danger"
                  />
                </div>
              </Col>

              <Col xs={12} md={6} xl={4}>
                <div className="fw-bold mb-2 small text-muted">
                  Event Segmentation:
                </div>
                <div className="d-flex gap-2">
                  <Form.Select
                    className="form-control"
                    value={selEventType}
                    onChange={(e) => {
                      setSelEventType(e.target.value);
                      setSelEventTitle("");
                    }}
                  >
                    <option value="">All Event Types</option>
                    {uniqueEventTypes.map((type, idx) => (
                      <option key={idx} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    className="form-control"
                    value={selEventTitle}
                    onChange={(e) => setSelEventTitle(e.target.value)}
                  >
                    <option value="">All Event Names</option>
                    {uniqueEventTitles.map((title, idx) => (
                      <option key={idx} value={title}>
                        {title}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </Col>

              <Col xs={12} md={6} xl={4}>
                <div className="fw-bold mb-2 small text-muted">
                  Global Search & Range:
                </div>
                <div className="d-flex gap-2">
                  <input
                    className="form-control"
                    type="search"
                    placeholder="Search anything..."
                    aria-label="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Form.Select
                    className="form-control"
                    style={{ minWidth: "120px" }}
                    aria-label="selDay"
                    value={selDay}
                    onChange={(e) => setSelDay(e.target.value)}
                  >
                    <option value="">Any Time</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="7days">7 Days</option>
                    <option value="14days">14 Days</option>
                  </Form.Select>
                </div>
              </Col>
            </Row>
          </div>

          <div
            className="mb-4 table-responsive"
            style={{
              overflowX: "auto",
              width: "100%",
              maxWidth: "100vw",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table className="text-center align-middle" striped bordered hover>
              <thead>
                <tr className="bg-light">
                  <th className="text-nowrap" style={{ width: "50px" }}>
                    Sr No.
                  </th>
                  <th className="text-nowrap" style={{ minWidth: "200px" }}>
                    Date
                  </th>
                  <th style={{ minWidth: "350px" }}>Event Title</th>
                  <th style={{ minWidth: "350px" }}>Participant Details</th>
                  <th style={{ minWidth: "300px" }}>Professional Info</th>
                  <th style={{ width: "100px" }}>ID Proof</th>
                  <th style={{ width: "120px" }}>Status</th>
                  <th style={{ width: "280px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentFiltered.length > 0 ? (
                  currentFiltered.map((item, index) => (
                    <tr key={item._id}>
                      <td>{filteredData.length - indexOfFirstItem - index}</td>
                      <td>{formatDateTimeStandard(item.createdAt)}</td>
                      <td className="text-start">
                        <strong>{item.eventId?.title || "N/A"}</strong>
                        <br />
                        <small className="text-muted">
                          {item.eventId?.eventType}
                        </small>
                      </td>
                      <td className="text-start">
                        <strong>{item.fullName}</strong>
                        <br />
                        <small>
                          {item.gender} | {item.email}
                        </small>
                        <br />
                        <small>{item.mobileNumber}</small>
                        <br />
                        <small>
                          {item.city}, {item.country}
                        </small>
                      </td>
                      <td className="text-start">
                        <strong>{item.organizationName}</strong>
                        <br />
                        <small>{item.professionalDesignation}</small>
                        <br />
                        <small>
                          {item.professionalCategory}{" "}
                          {item.professionalCategoryOther
                            ? `(${item.professionalCategoryOther})`
                            : ""}
                        </small>
                      </td>
                      <td>
                        {item.idProof && (
                          <AdvancedPopUpModalButton
                            title="View ID"
                            fileLink={item.idProof}
                            variant="info"
                          />
                        )}
                      </td>
                      <td>
                        <Badge
                          bg={
                            item.uiStatus === "Approved"
                              ? "success"
                              : item.uiStatus === "Rejected"
                                ? "danger"
                                : "warning"
                          }
                        >
                          {item.uiStatus}
                        </Badge>

                        {item.eventId &&
                        (item.eventId.joinRequests?.some(
                          (req) => req.email === item.email,
                        ) ||
                          item.eventId.participantsAllowed?.some(
                            (p) => p.email === item.email,
                          )) ? (
                          <Badge bg="info" className="d-block mt-2">
                            Attempted / Attended
                          </Badge>
                        ) : (
                          <Badge bg="secondary" className="d-block mt-2">
                            Not Attended
                          </Badge>
                        )}
                      </td>
                      <td>
                        {item.uiStatus === "Approved" ? (
                          item.eventId?.isRegistrationClosed ? (
                            !item.certificateBlob && !item.certificate ? (
                              <div className="d-flex justify-content-center pt-2">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleGenerateEventCert(item)}
                                >
                                  Generate Certificate
                                </Button>
                              </div>
                            ) : item.certificateBlob && !item.certificate ? (
                              <div className="d-flex flex-column align-items-center justify-content-center pt-2 gap-2">
                                {item.certificateUrl && (
                                  <Button
                                    variant="info"
                                    size="sm"
                                    onClick={() => {
                                      setCertModalItem(item);
                                      setShowCertModal(true);
                                    }}
                                    className="text-white"
                                  >
                                    View / Manage Certificate
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <div className="d-flex flex-column align-items-center justify-content-center pt-2 gap-2">
                                {item.certificate && (
                                  <PopUpModalButton
                                    title="View Approved Certificate"
                                    fileLink={item.certificate}
                                    variant="primary"
                                  />
                                )}
                                <div className="text-start mt-1">
                                  <small>
                                    <strong>Remark:</strong> {item.remark}
                                  </small>
                                  <br />
                                  <Badge bg="info">Certificate Issued</Badge>
                                </div>
                              </div>
                            )
                          ) : (
                            <div className="text-start">
                              <small>
                                <strong>Remark:</strong> {item.remark}
                              </small>
                            </div>
                          )
                        ) : item.uiStatus === "Processing" ? (
                          <div style={{ minWidth: "200px" }}>
                            <>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Remark"
                                className="mb-2 rounded-0"
                                value={item.remark}
                                name="remark"
                                onChange={(e) =>
                                  handleInputChange(
                                    item._id,
                                    "remark",
                                    e.target.value,
                                  )
                                }
                              />
                              <div className="d-flex justify-content-center">
                                {appDisApi ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    variant="success"
                                  />
                                ) : (
                                  <ConfirmModal
                                    handleApproveRejectEco={
                                      handleApproveRejectBooking
                                    }
                                    userId={item._id}
                                  />
                                )}
                              </div>
                            </>
                          </div>
                        ) : (
                          <div className="text-start">
                            <small>
                              <strong>Remark:</strong> {item.remark}
                            </small>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8">No Bookings Found</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-start mt-2">
              <Pagination>
                <Pagination.Prev
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                />
                {Array.from({ length: endPage - startPage + 1 }, (_, index) => (
                  <Pagination.Item
                    key={index + startPage}
                    active={index + startPage === currentPage}
                    onClick={() => handlePageChange(index + startPage)}
                  >
                    {index + startPage}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </div>
      </MasterNavigationBar>
      {/* Pagination Container End */}

      {/* Certificate Preview Modal */}
      <Modal
        show={showCertModal}
        onHide={() => {
          setShowCertModal(false);
          setCertModalItem(null);
        }}
        size="xl"
        centered
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: "#0b3b5d", color: "white" }}
          className="text-white"
        >
          <Modal.Title>Preview & Manage Generated Certificate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8} lg={9}>
              <div className="border rounded shadow-sm h-100">
                {certModalItem?.certificateUrl ? (
                  <iframe
                    src={`${certModalItem.certificateUrl}#zoom=56`}
                    title="Certificate Preview"
                    width="100%"
                    height="700px"
                    frameBorder="0"
                  />
                ) : (
                  <div className="p-5 text-center">Loading PDF preview...</div>
                )}
              </div>
            </Col>

            <Col
              md={4}
              lg={3}
              className="bg-light p-3 rounded border border-start-0"
            >
              <div className="mb-4">
                <p className="mb-1">
                  <strong>Participant:</strong>
                  <br />
                  {certModalItem?.fullName}
                </p>
                <p className="mb-3 text-muted small">
                  Adjust custom properties and verify the preview, then
                  finalize.
                </p>
                <div className="d-flex flex-column gap-2">
                  <Button
                    variant="warning"
                    onClick={() => handleGenerateEventCert(certModalItem)}
                    disabled={appDisApi}
                    className="w-100"
                  >
                    Regenerate PDF
                  </Button>
                  <Button
                    variant="success"
                    onClick={() => handleApproveCertificate(certModalItem._id)}
                    disabled={appDisApi}
                    className="w-100 shadow"
                  >
                    {appDisApi ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      "Approve & Uplink"
                    )}
                  </Button>
                </div>
              </div>

              <hr />

              <div className="mb-3">
                <h6 className="mb-3 fw-bold text-dark">Certificate Options</h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <label className="small fw-bold mb-1">
                      Custom Issue Date
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customIssueDate}
                      onChange={(e) => setCustomIssueDate(e.target.value)}
                    />
                  </div>
                  {/* <div>
                    <label className="small fw-bold mb-1">
                      Upload Signature (PNG/JPG)
                    </label>
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      className="form-control form-control-sm"
                      onChange={handleSignatureUpload}
                    />
                  </div> */}
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </Layout>
  );
};

export default ViewEventBooking;
